import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 24px;
  padding: 32px 28px;
  max-width: 420px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Icon = styled.div`
  font-size: 56px;
  text-align: center;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 8px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 24px;
  text-align: center;
  line-height: 1.6;
`;

const Highlights = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
`;

const HighlightItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const HighlightIcon = styled.span`
  font-size: 20px;
  flex-shrink: 0;
`;

const HighlightText = styled.div`
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  cursor: pointer;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  margin-top: 2px;
  accent-color: #2DBCAF;
  cursor: pointer;
`;

const CheckboxLabel = styled.span`
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
`;

const PolicyLink = styled.a`
  color: #2DBCAF;
  text-decoration: underline;
  cursor: pointer;
  
  &:hover {
    color: #26a89d;
  }
`;

const Button = styled.button<{ $primary?: boolean; disabled?: boolean }>`
  width: 100%;
  padding: 16px 24px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 12px;
  
  ${p => p.$primary ? `
    background: #2DBCAF;
    color: #fff;
    border: none;
    
    &:hover:not(:disabled) {
      background: #26a89d;
    }
    
    &:disabled {
      background: #94a3b8;
      cursor: not-allowed;
    }
  ` : `
    background: #fff;
    color: #64748b;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background: #f8fafc;
    }
  `}
`;

const ErrorMsg = styled.div`
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  padding: 12px;
  border-radius: 10px;
  font-size: 13px;
  margin-bottom: 16px;
  text-align: center;
`;

interface Props {
  onAccept: () => void;
  onDecline: () => void;
}

const PrivacyConsentModal: React.FC<Props> = ({ onAccept, onDecline }) => {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    if (!agreed) return;
    
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch('/api/auth/accept-privacy-policy', {
        method: 'POST',
        credentials: 'include',
      });
      
      if (res.ok) {
        onAccept();
      } else {
        setError('Gagal menyimpan persetujuan. Coba lagi.');
      }
    } catch {
      setError('Terjadi kesalahan. Coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPolicy = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open('/privacy-policy', '_blank');
  };

  return (
    <Overlay>
      <Modal>
        <Icon>🔒</Icon>
        <Title>Kebijakan Privasi</Title>
        <Subtitle>
          Sebelum melanjutkan, mohon baca dan setujui kebijakan privasi kami
        </Subtitle>

        {error && <ErrorMsg>{error}</ErrorMsg>}

        <Highlights>
          <HighlightItem>
            <HighlightIcon>👨‍👩‍👧</HighlightIcon>
            <HighlightText>
              Aplikasi ini digunakan oleh <strong>orang tua</strong> untuk mendukung tumbuh kembang anak
            </HighlightText>
          </HighlightItem>
          <HighlightItem>
            <HighlightIcon>📝</HighlightIcon>
            <HighlightText>
              Data anak (nama, tanggal lahir) dikumpulkan dengan persetujuan orang tua
            </HighlightText>
          </HighlightItem>
          <HighlightItem>
            <HighlightIcon>🔐</HighlightIcon>
            <HighlightText>
              Data Anda aman dan tidak akan dijual ke pihak ketiga
            </HighlightText>
          </HighlightItem>
          <HighlightItem>
            <HighlightIcon>🗑️</HighlightIcon>
            <HighlightText>
              Anda dapat menghapus data kapan saja melalui pengaturan
            </HighlightText>
          </HighlightItem>
        </Highlights>

        <CheckboxRow>
          <Checkbox
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <CheckboxLabel>
            Saya telah membaca dan menyetujui{' '}
            <PolicyLink onClick={handleViewPolicy}>
              Kebijakan Privasi
            </PolicyLink>
            {' '}Kindergame
          </CheckboxLabel>
        </CheckboxRow>

        <Button 
          $primary 
          onClick={handleAccept} 
          disabled={!agreed || loading}
        >
          {loading ? 'Memproses...' : 'Setuju & Lanjutkan'}
        </Button>
        
        <Button onClick={onDecline}>
          Tidak Setuju (Keluar)
        </Button>
      </Modal>
    </Overlay>
  );
};

export default PrivacyConsentModal;
